class EliseCartRecommendations extends HTMLElement {
  static get observedAttributes() {
    return ['data-product-id'];
  }

  #observer = null;
  #hasEnteredViewport = false;
  #loading = false;
  #loadedProductId = '';
  #requestVersion = 0;

  connectedCallback() {
    if ('IntersectionObserver' in window) {
      this.#observer?.disconnect();
      this.#observer = new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#hasEnteredViewport = true;
        observer.disconnect();
        this.#loadRecommendations();
      }, { rootMargin: '160px 0px' });
      this.#observer.observe(this);
    } else {
      this.#hasEnteredViewport = true;
      this.#loadRecommendations();
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#requestVersion += 1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-product-id' || oldValue === newValue) return;

    this.#loadedProductId = '';
    this.#requestVersion += 1;
    this.hidden = !newValue;
    if (!newValue) return;

    this.innerHTML = '<div class="elise-cart-recommendations__loading"><span class="elise-cart-recommendations__loading-dot" aria-hidden="true"></span><span>Passende Empfehlungen werden geladen …</span></div>';
    if (this.#hasEnteredViewport) this.#loadRecommendations();
  }

  async #loadRecommendations() {
    const productId = this.dataset.productId;
    if (!productId || !this.dataset.url || !this.dataset.sectionId || this.#loading) return;
    if (productId === this.#loadedProductId) return;

    this.#loading = true;
    const requestVersion = ++this.#requestVersion;

    try {
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('intent', 'related');

      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Recommendation request failed: ' + response.status);

      const responseText = await response.text();
      const documentFromResponse = new DOMParser().parseFromString(responseText, 'text/html');
      const content = documentFromResponse.querySelector('.elise-cart-recommendations__content');

      if (!this.isConnected || requestVersion !== this.#requestVersion) return;
      this.#loadedProductId = productId;

      if (!content) {
        this.hidden = true;
        return;
      }

      this.replaceChildren(content);
      this.hidden = false;
    } catch (error) {
      if (this.isConnected && requestVersion === this.#requestVersion) {
        this.#loadedProductId = productId;
        this.hidden = true;
      }
    } finally {
      this.#loading = false;
      const currentProductId = this.dataset.productId;
      if (this.isConnected && this.#hasEnteredViewport && currentProductId && currentProductId !== this.#loadedProductId) {
        this.#loadRecommendations();
      }
    }
  }
}

if (!customElements.get('elise-cart-recommendations')) {
  customElements.define('elise-cart-recommendations', EliseCartRecommendations);
}
